import { ChevronLeft, ChevronRight, Copy, Download, Forward, Pause, Play, Reply, Share2, Volume2, VolumeX, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Avatar } from '../../design/primitives.tsx';
import { playableVideo, sizedImage, videoPoster } from '../../platform/images.ts';
import { useToast } from '../../shell/toast.tsx';
import { attachmentUrl, clampPan, downloadName, fitSize, originTransform, zoomAround, type Size } from './media.ts';
import { useProcessingRetry } from '../video/useProcessingRetry.ts';
import styles from './Viewer.module.css';

// One full-screen viewer for every photo and video in the app (chats,
// posts, listings, profile pictures), like Instagram and WhatsApp: it
// zooms out of the picture you tapped, pinch or double-tap to zoom, drag to
// pan, swipe sideways between items, swipe down / ✕ / Esc / Back to close.
// Nothing opens outside the app.

export type ViewerItem = {
  kind: 'photo' | 'video';
  url: string;
  poster?: string | null;
  alt?: string;
  from?: { name: string; photoUrl?: string | null } | null;
  at?: string | null;
  onForward?: () => void;
  onReply?: () => void;
};
type Open = { items: ViewerItem[]; index?: number; origin?: Element | null };
type Viewer = { open: (request: Open) => void };

const ViewerContext = createContext<Viewer | null>(null);
const HISTORY_KEY = 'guilditeViewer';

export function useViewer(): Viewer {
  return useContext(ViewerContext) ?? { open: () => undefined };
}

export function ViewerProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<(Open & { key: number }) | null>(null);
  const closing = useRef<(() => void) | null>(null);
  const open = useCallback((next: Open) => {
    if (!next.items.length) return;
    // Back (phone button, browser) closes the viewer instead of leaving the page.
    window.history.pushState({ ...(window.history.state as object | null), [HISTORY_KEY]: true }, '');
    setRequest({ ...next, key: Date.now() });
  }, []);
  useEffect(() => {
    if (!request) return;
    const back = () => closing.current?.();
    window.addEventListener('popstate', back);
    return () => window.removeEventListener('popstate', back);
  }, [request]);
  const value = useMemo(() => ({ open }), [open]);
  return (
    <ViewerContext.Provider value={value}>
      {children}
      {request ? (
        <MediaViewer key={request.key} items={request.items} start={Math.min(request.index ?? 0, request.items.length - 1)} origin={request.origin ?? null}
          register={close => { closing.current = close; }}
          requestClose={() => {
            if ((window.history.state as Record<string, unknown> | null)?.[HISTORY_KEY]) window.history.back();
            else closing.current?.();
          }}
          onDone={() => setRequest(null)} />
      ) : null}
    </ViewerContext.Provider>
  );
}

const screenSize = (): Size => ({ width: window.innerWidth, height: window.innerHeight });
const MAX_SCALE = 5;
const DOUBLE_TAP_MS = 280;
const ANIMATION_MS = 220;
const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

type View = { scale: number; x: number; y: number };
const REST: View = { scale: 1, x: 0, y: 0 };

function MediaViewer({ items, start, origin, register, requestClose, onDone }: {
  items: ViewerItem[]; start: number; origin: Element | null; register: (close: () => void) => void; requestClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [index, setIndex] = useState(start);
  const [view, setView] = useState<View>(REST);
  const [swipeX, setSwipeX] = useState(0);
  const [dropY, setDropY] = useState(0);
  const [chrome, setChrome] = useState(true);
  const [animate, setAnimate] = useState(false);
  const [phase, setPhase] = useState<'opening' | 'open' | 'closing'>('opening');
  const [screen, setScreen] = useState(screenSize);
  const [naturals, setNaturals] = useState<Record<string, Size>>(() => {
    const image = origin instanceof HTMLImageElement && origin.naturalWidth ? origin : origin?.querySelector('img');
    const item = items[start];
    return image && item && image.naturalWidth ? { [item.url]: { width: image.naturalWidth, height: image.naturalHeight } } : {};
  });
  const [originBox] = useState(() => origin?.getBoundingClientRect() ?? null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ mode: 'none' | 'pan' | 'swipe' | 'drop' | 'pinch'; x: number; y: number; at: number; view: View; distance?: number; mid?: { x: number; y: number } } | null>(null);
  const lastTap = useRef<{ at: number; x: number; y: number } | null>(null);
  const tapTimer = useRef(0);
  const item = items[index]!;
  const fitted = fitSize(naturals[item.url] ?? null, screen);

  const finish = useCallback(() => {
    setAnimate(true);
    setPhase('closing');
    setView(REST);
    window.setTimeout(onDone, reduceMotion() ? 0 : ANIMATION_MS);
    if (origin instanceof HTMLElement) origin.focus?.({ preventScroll: true });
  }, [onDone, origin]);
  useEffect(() => register(finish), [register, finish]);

  // Zoom out of the tapped picture.
  useLayoutEffect(() => {
    const overflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    closeButton.current?.focus({ preventScroll: true });
    let frame = requestAnimationFrame(() => { frame = requestAnimationFrame(() => { setAnimate(true); setPhase('open'); }); });
    const resize = () => setScreen(screenSize());
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', resize); document.documentElement.style.overflow = overflow; window.clearTimeout(tapTimer.current); };
  }, []);

  const go = useCallback((next: number) => {
    const target = Math.max(0, Math.min(items.length - 1, next));
    setAnimate(true);
    setSwipeX(0);
    setView(REST);
    setIndex(target);
  }, [items.length]);

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); requestClose(); }
      if (event.key === 'ArrowRight') go(index + 1);
      if (event.key === 'ArrowLeft') go(index - 1);
      if (event.key === '+' || event.key === '=') setView(current => ({ ...current, scale: Math.min(MAX_SCALE, current.scale * 1.5) }));
      if (event.key === '-') setView(current => (current.scale / 1.5 <= 1 ? REST : { ...current, scale: current.scale / 1.5 }));
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [go, index, requestClose]);

  const centre = (x: number, y: number) => ({ x: x - screen.width / 2, y: y - screen.height / 2 });
  const settle = (next: View) => {
    if (next.scale <= 1.01) return REST;
    return { scale: next.scale, ...clampPan(next.x, next.y, next.scale, fitted, screen) };
  };
  const zoomAt = (x: number, y: number) => {
    setAnimate(true);
    setView(current => (current.scale > 1 ? REST : settle(zoomAround(centre(x, y), current, 2.5))));
  };

  function down(event: ReactPointerEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest('button, input, a')) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    setAnimate(false);
    const points = [...pointers.current.values()];
    if (points.length === 2 && item.kind === 'photo') {
      const [a, b] = points as [{ x: number; y: number }, { x: number; y: number }];
      gesture.current = { mode: 'pinch', x: 0, y: 0, at: event.timeStamp, view, distance: Math.hypot(a.x - b.x, a.y - b.y), mid: centre((a.x + b.x) / 2, (a.y + b.y) / 2) };
      setSwipeX(0);
      setDropY(0);
    } else if (points.length === 1) {
      gesture.current = { mode: view.scale > 1 ? 'pan' : 'none', x: event.clientX, y: event.clientY, at: event.timeStamp, view };
    }
  }

  function move(event: ReactPointerEvent<HTMLDivElement>) {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const now = gesture.current;
    if (!now) return;
    if (now.mode === 'pinch') {
      const [a, b] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      if (!a || !b || !now.distance || !now.mid) return;
      const scale = Math.max(0.8, Math.min(MAX_SCALE, now.view.scale * Math.hypot(a.x - b.x, a.y - b.y) / now.distance));
      const zoomed = zoomAround(now.mid, now.view, scale);
      const mid = centre((a.x + b.x) / 2, (a.y + b.y) / 2);
      setView({ scale, x: zoomed.x + mid.x - now.mid.x, y: zoomed.y + mid.y - now.mid.y });
      return;
    }
    const dx = event.clientX - now.x;
    const dy = event.clientY - now.y;
    if (now.mode === 'pan') { setView({ ...now.view, ...clampPan(now.view.x + dx, now.view.y + dy, now.view.scale, fitted, screen) }); return; }
    if (now.mode === 'none' && Math.hypot(dx, dy) > 8) now.mode = Math.abs(dx) > Math.abs(dy) ? 'swipe' : dy > 0 ? 'drop' : 'none';
    if (now.mode === 'swipe') {
      const edge = (index === 0 && dx > 0) || (index === items.length - 1 && dx < 0);
      setSwipeX(edge ? dx / 3 : dx);
    }
    if (now.mode === 'drop') setDropY(Math.max(0, dy));
  }

  function up(event: ReactPointerEvent<HTMLDivElement>) {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.delete(event.pointerId);
    const now = gesture.current;
    setAnimate(true);
    if (!now) return;
    if (now.mode === 'pinch') {
      setView(current => settle(current));
      const left = [...pointers.current.values()][0];
      gesture.current = left ? { mode: 'pan', x: left.x, y: left.y, at: event.timeStamp, view: settle(view) } : null;
      return;
    }
    gesture.current = null;
    const dx = event.clientX - now.x;
    const dy = event.clientY - now.y;
    const quick = event.timeStamp - now.at < 250;
    if (now.mode === 'swipe') {
      setSwipeX(0);
      if (Math.abs(dx) > screen.width * 0.18 || (quick && Math.abs(dx) > 30)) go(index + (dx < 0 ? 1 : -1));
      return;
    }
    if (now.mode === 'drop') {
      if (dy > 120 || (quick && dy > 50)) requestClose();
      else setDropY(0);
      return;
    }
    // A press that barely moved is a tap, zoomed or not (double-tap zooms back out).
    if (Math.hypot(dx, dy) > 8) return;
    tap(event.clientX, event.clientY, event.timeStamp, event.pointerType, event.target as HTMLElement);
  }

  function tap(x: number, y: number, at: number, pointer: string, target: HTMLElement) {
    const before = lastTap.current;
    if (before && at - before.at < DOUBLE_TAP_MS && Math.hypot(x - before.x, y - before.y) < 30) {
      window.clearTimeout(tapTimer.current);
      lastTap.current = null;
      if (item.kind === 'photo') zoomAt(x, y);
      return;
    }
    lastTap.current = { at, x, y };
    window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      lastTap.current = null;
      // With a mouse, a click beside the photo closes; on a phone a tap shows or hides the bars.
      if (pointer === 'mouse') { if (!target.closest('img, video')) requestClose(); return; }
      if (item.kind === 'video') videoRef.current?.toggle();
      else setChrome(value => !value);
    }, DOUBLE_TAP_MS);
  }

  const videoRef = useRef<{ toggle: () => void } | null>(null);
  const box = originBox;
  const atOrigin = box && index === start && (phase === 'opening' || (phase === 'closing' && view.scale === 1)) && !reduceMotion();
  const mediaTransform = atOrigin ? originTransform({ left: box.left, top: box.top, width: box.width, height: box.height }, fitted, screen)
    : `translate(${view.x}px, ${view.y + dropY}px) scale(${view.scale * (dropY ? Math.max(0.7, 1 - dropY / 1000) : 1)})`;
  const fade = phase === 'open' ? Math.max(0.2, 1 - dropY / 400) : 0;

  async function file(): Promise<File> {
    const response = await fetch(item.url, { mode: 'cors' });
    if (!response.ok) throw new Error(String(response.status));
    const blob = await response.blob();
    return new File([blob], downloadName(item, blob.type), { type: blob.type });
  }
  async function download() {
    try {
      const got = await file();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(got);
      link.download = got.name;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
      toast.show('Saved to your downloads.');
    } catch {
      // Cloudinary sends it as a named download; the app stays where it is.
      const link = document.createElement('a');
      link.href = attachmentUrl(item.url, downloadName(item));
      link.download = downloadName(item);
      link.click();
    }
  }
  async function share() {
    try {
      const got = await file();
      if (navigator.canShare?.({ files: [got] })) { await navigator.share({ files: [got] }); return; }
      if (navigator.share) { await navigator.share({ url: item.url }); return; }
      toast.show('Sharing isn’t available on this device. Use Download instead.');
    } catch (failure) {
      if ((failure as Error)?.name !== 'AbortError') toast.show('That didn’t share. Try again.');
    }
  }
  async function copy() {
    try {
      const got = await file();
      const bitmap = await createImageBitmap(got);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
      const png = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!png) throw new Error('no image');
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      toast.show('Photo copied.');
    } catch {
      toast.show('That didn’t copy. Try Download instead.');
    }
  }
  const canCopy = item.kind === 'photo' && typeof ClipboardItem !== 'undefined' && Boolean(navigator.clipboard?.write) && (window.matchMedia?.('(pointer: fine)').matches ?? false);
  const when = item.at ? new Date(item.at) : null;
  const whenText = when && !Number.isNaN(when.getTime())
    ? when.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', ...(when.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) }) : '';
  const act = (work: () => void) => () => { requestClose(); work(); };

  return createPortal(
    <div className={[styles.viewer, chrome ? '' : styles.bare].join(' ')} role="dialog" aria-modal="true"
      aria-label={`${item.kind === 'video' ? 'Video' : 'Photo'}${items.length > 1 ? ` ${index + 1} of ${items.length}` : ''}${item.from ? ` from ${item.from.name}` : ''}`}>
      <div className={styles.backdrop} style={{ opacity: fade, transition: animate ? `opacity ${ANIMATION_MS}ms` : 'none' }} aria-hidden="true" />
      <div className={styles.stage} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        onWheel={event => { if (item.kind !== 'photo') return; setAnimate(false); const scale = Math.max(1, Math.min(MAX_SCALE, view.scale * (event.deltaY < 0 ? 1.15 : 1 / 1.15))); setView(settle(zoomAround(centre(event.clientX, event.clientY), view, scale))); }}>
        <div className={styles.track} style={{ transform: `translateX(calc(${-index * 100}% + ${swipeX}px))`, transition: animate && !swipeX ? `transform ${ANIMATION_MS}ms ease-out` : 'none' }}>
          {items.map((entry, at) => (
            <div key={`${entry.url}:${at}`} className={styles.slide} aria-hidden={at !== index}>
              {Math.abs(at - index) <= 1 ? (
                <div className={styles.media} style={at === index ? { transform: mediaTransform, opacity: phase === 'closing' && !atOrigin ? 0 : 1, transition: animate ? `transform ${ANIMATION_MS}ms ease-out, opacity ${ANIMATION_MS}ms` : 'none' } : undefined}>
                  {entry.kind === 'video'
                    ? <ViewerVideo item={entry} active={at === index && phase !== 'closing'} controlRef={at === index ? videoRef : null} chrome={chrome} />
                    : <img src={sizedImage(entry.url, 2000)} alt={entry.alt ?? (entry.from ? `Photo from ${entry.from.name}` : 'Photo')} draggable={false}
                      onLoad={event => { const image = event.currentTarget; if (!naturals[entry.url]) setNaturals(current => ({ ...current, [entry.url]: { width: image.naturalWidth, height: image.naturalHeight } })); }} />}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.top} style={{ opacity: phase === 'open' && !dropY ? undefined : 0 }}>
        <button ref={closeButton} type="button" className={styles.icon} aria-label="Close" onClick={requestClose}><X size={24} aria-hidden="true" /></button>
        {item.from ? <Avatar name={item.from.name} photoUrl={item.from.photoUrl ?? null} size={34} /> : null}
        <span className={styles.who}>
          <b>{item.from?.name ?? (item.kind === 'video' ? 'Video' : 'Photo')}</b>
          <span>{[whenText, items.length > 1 ? `${index + 1} of ${items.length}` : ''].filter(Boolean).join(' · ')}</span>
        </span>
      </div>

      <div className={styles.bottom} role="toolbar" aria-label="Photo actions" style={{ opacity: phase === 'open' && !dropY ? undefined : 0 }}>
        <button type="button" onClick={() => void download()}><Download size={22} aria-hidden="true" /><span>Download</span></button>
        <button type="button" onClick={() => void share()}><Share2 size={22} aria-hidden="true" /><span>Share</span></button>
        {item.onForward ? <button type="button" onClick={act(item.onForward)}><Forward size={22} aria-hidden="true" /><span>Forward</span></button> : null}
        {item.onReply ? <button type="button" onClick={act(item.onReply)}><Reply size={22} aria-hidden="true" /><span>Reply</span></button> : null}
        {canCopy ? <button type="button" onClick={() => void copy()}><Copy size={22} aria-hidden="true" /><span>Copy</span></button> : null}
      </div>

      {items.length > 1 ? (
        <>
          <button type="button" className={[styles.arrow, styles.prev].join(' ')} onClick={() => go(index - 1)} disabled={index === 0} aria-label="Previous"><ChevronLeft size={28} aria-hidden="true" /></button>
          <button type="button" className={[styles.arrow, styles.next].join(' ')} onClick={() => go(index + 1)} disabled={index === items.length - 1} aria-label="Next"><ChevronRight size={28} aria-hidden="true" /></button>
        </>
      ) : null}
    </div>,
    document.body,
  );
}

const clockText = (value: number) => (Number.isFinite(value) ? `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}` : '0:00');

// A video in the viewer: plays with sound when opened (it was a tap), tap
// to pause or play, mute, a scrub bar and the time. No browser controls.
function ViewerVideo({ item, active, controlRef, chrome }: { item: ViewerItem; active: boolean; controlRef: { current: { toggle: () => void } | null } | null; chrome: boolean }) {
  const element = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const retry = useProcessingRetry();
  const toggle = useCallback(() => {
    const video = element.current;
    if (!video) return;
    // Browsers may refuse sound without a tap first: then it plays muted.
    if (video.paused) Promise.resolve(video.play()).catch(() => { video.muted = true; setMuted(true); return video.play(); }).catch(() => undefined);
    else video.pause();
  }, []);
  useEffect(() => {
    if (controlRef) controlRef.current = { toggle };
  }, [controlRef, toggle]);
  useEffect(() => {
    const video = element.current;
    if (!video) return;
    if (active) toggle();
    else video.pause();
  }, [active, toggle]);
  return (
    <div className={styles.videoBox}>
      <video ref={element} src={playableVideo(item.url)} poster={item.poster ?? videoPoster(item.url) ?? undefined} playsInline loop={false} preload="metadata" muted={muted}
        aria-label={item.alt ?? (item.from ? `Video from ${item.from.name}` : 'Video')}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} onError={retry.onError}
        onLoadedData={event => { retry.onReady(); if (active && event.currentTarget.paused) toggle(); }}
        onTimeUpdate={event => setTime(event.currentTarget.currentTime)} onLoadedMetadata={event => setDuration(event.currentTarget.duration)} onDurationChange={event => { if (Number.isFinite(event.currentTarget.duration)) setDuration(event.currentTarget.duration); }} />
      {retry.preparing ? <span className={styles.preparing} role="status">Getting the video ready…</span> : !playing ? <span className={styles.bigPlay} aria-hidden="true"><Play size={34} fill="currentColor" /></span> : null}
      <div className={styles.videoBar} style={{ opacity: chrome || !playing ? 1 : 0 }}>
        <button type="button" aria-label={playing ? 'Pause' : 'Play'} onClick={toggle}>{playing ? <Pause size={20} fill="currentColor" aria-hidden="true" /> : <Play size={20} fill="currentColor" aria-hidden="true" />}</button>
        <span className={styles.time}>{clockText(time)}</span>
        <input type="range" className={styles.scrub} aria-label="Position" min={0} max={Number.isFinite(duration) ? duration : 0} step={0.1} value={Math.min(time, duration || 0)}
          onChange={event => { const video = element.current; if (video) { video.currentTime = Number(event.target.value); setTime(video.currentTime); } }} />
        <span className={styles.time}>{clockText(duration)}</span>
        <button type="button" aria-label={muted ? 'Turn sound on' : 'Mute'} onClick={() => setMuted(value => !value)}>{muted ? <VolumeX size={20} aria-hidden="true" /> : <Volume2 size={20} aria-hidden="true" />}</button>
      </div>
    </div>
  );
}

// A profile picture or shop logo you can tap to see full screen. Without a
// picture it's the plain monogram, not a button.
export function PictureButton({ url, name, className, children }: { url: string | null | undefined; name: string; className?: string; children: ReactNode }) {
  const viewer = useViewer();
  if (!url) return <span className={className}>{children}</span>;
  return (
    <button type="button" className={[className, styles.pictureButton].filter(Boolean).join(' ')} aria-label={`See ${name}’s picture`}
      onClick={event => viewer.open({ items: [{ kind: 'photo', url, alt: `${name}’s picture`, from: { name } }], origin: event.currentTarget.querySelector('img') })}>
      {children}
    </button>
  );
}
